import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { Search, Briefcase, RefreshCw, Mail, Trash2, ChevronDown, Telescope, Play, X, Link2, Hammer, Ban } from "lucide-react";
import { LinkedInJobsProvider, useLinkedInJobs } from "./modal/LinkedInJobsContext";
import type { RemoteScopeFilter } from "./modal/LinkedInJobsContext";
import { STATUS_OPTIONS, fetchAlerts, type JobStatus } from "./api/LinkedInJobsApi";
import JobCard from "./ui/JobCard";
import JobDetailModal from "./ui/JobDetailModal";
import CleanupConfirmModal from "./ui/CleanupConfirmModal";
import AddJobByUrlModal from "./ui/AddJobByUrlModal";
import AppSelect from "shared/AppSelect";
import WatchlistLeadsTab from "features/watchlist/ui/WatchlistLeadsTab";
import TheirStackJobsPage from "features/theirstack-jobs/TheirStackJobsPage";
import UpworkJobsPage from "features/upwork-jobs/UpworkJobsPage";
import ViewToggle from "shared/ViewToggle";
import StatusKanbanBoard, { type KanbanColumn } from "shared/StatusKanbanBoard";
import BlockedCompaniesTab from "features/blocked-companies/BlockedCompaniesTab";

const JOBS_VIEW_KEY = "hrms_linkedin_jobs_view";
const TERMINAL_STATUSES = new Set(["archived", "not_relevant"]);
const BOARD_COLUMNS: KanbanColumn[] = STATUS_OPTIONS.map((o) => ({
  key: o.value,
  label: o.label,
  isTerminal: TERMINAL_STATUSES.has(o.value),
}));

const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Internship", "Temporary"];
const SENIORITY_LEVELS = [
  "Internship", "Entry level", "Associate", "Mid-Senior level", "Director", "Executive",
];

const FILTER_STATUS_OPTIONS = [{ value: "", label: "All" }, ...STATUS_OPTIONS];

const STATUS_CHIP_SELECTED: Record<string, string> = {
  "":             "bg-slate-500 text-white border-slate-400",
  new:            "bg-blue-500 text-white border-blue-400",
  reviewing:      "bg-violet-500 text-white border-violet-400",
  applied:        "bg-sky-500 text-sky-950 border-sky-400",
  interviewing:   "bg-amber-500 text-amber-950 border-amber-400",
  offer_received: "bg-teal-500 text-white border-teal-400",
  hired:          "bg-emerald-500 text-emerald-950 border-emerald-400",
  rejected:       "bg-red-500 text-white border-red-400",
  not_relevant:   "bg-zinc-500 text-white border-zinc-400",
  archived:       "bg-zinc-700 text-zinc-200 border-zinc-600",
};

interface Props {
  isAdmin: boolean;
  isTechnicalManager?: boolean;
}

export default function JobBoardPage({ isAdmin, isTechnicalManager = false }: Props) {
  return (
    <LinkedInJobsProvider isAdmin={isAdmin}>
      <JobBoardBody isTechnicalManager={isTechnicalManager} />
    </LinkedInJobsProvider>
  );
}

function JobBoardBody({ isTechnicalManager }: { isTechnicalManager: boolean }) {
  const {
    isAdmin,
    jobs, totalCount, totalPages, page, loading, loadError,
    searchInput, employmentType, seniority,
    selectedManagerId, companyCanApplyOnly, techManagers, allEmployees,
    remoteScopeFilter, statusFilter,
    search,
    statusCounts,
    selectedJob,
    archivedPreview, archivedPreviewLoading,
    cleanupModal, cleanupExecuting, cleanupError,
    loadJobs, handleSearchSubmit, clearFilters,
    setSearchInput, setEmploymentType, setSeniority, setSelectedJob,
    setSelectedManagerId, setRemoteScopeFilter, setStatusFilter, setCompanyCanApplyOnly,
    openArchivedCleanup, openOldJobsCleanup, executeCleanup, closeCleanupModal,
    handleReassignAlert, handleStatusUpdate,
  } = useLinkedInJobs();

  const [pageTab, setPageTab] = useState<"all_jobs" | "email_alerts" | "watchlist_leads" | "upwork" | "blocked_companies">("all_jobs");
  const employeeNameById = useMemo(
    () => new Map(allEmployees.map((e) => [e.id, e.name])),
    [allEmployees]
  );
  const [view, setView] = useState<"list" | "board">(() =>
    localStorage.getItem(JOBS_VIEW_KEY) === "list" ? "list" : "board"
  );
  const switchView = (v: "list" | "board") => {
    setView(v);
    localStorage.setItem(JOBS_VIEW_KEY, v);
  };
  // Board shows every status as its own column, each paginated independently
  // (hide_archived only makes sense for the flat list) — keeps every other
  // filter in sync with the list view.
  const fetchBoardColumn = useCallback(async (statusKey: string, page: number, pageSize: number) => {
    const remote_scope_filter = remoteScopeFilter !== "all" ? remoteScopeFilter : undefined;
    const result = await fetchAlerts(
      {
        page,
        page_size: pageSize,
        status_filter: statusKey,
        hide_archived: false,
        employment_type: employmentType || undefined,
        seniority: seniority || undefined,
        search: search || undefined,
        remote_scope_filter,
        employee_id: isAdmin && selectedManagerId ? selectedManagerId : undefined,
        company_can_apply_only: companyCanApplyOnly || undefined,
      },
      isAdmin,
    );
    return { items: result.items, total: result.total_count };
  }, [employmentType, seniority, search, remoteScopeFilter, isAdmin, selectedManagerId, companyCanApplyOnly]);
  const [oldJobsDropdownOpen, setOldJobsDropdownOpen] = useState(false);
  const [showSetupVideo, setShowSetupVideo] = useState(false);
  const [showAddByUrl, setShowAddByUrl] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOldJobsDropdownOpen(false);
      }
    }
    if (oldJobsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [oldJobsDropdownOpen]);

  // Only fetch alerts data when that tab is actually visible — avoids an unused
  // request when the page lands on/switches to "Other Jobs" or "Posts Leads".
  useEffect(() => {
    if (pageTab === "email_alerts") loadJobs(1);
  }, [pageTab, loadJobs]);

  const pageTabs: { id: "all_jobs" | "email_alerts" | "watchlist_leads" | "upwork" | "blocked_companies"; label: string; icon: React.ReactNode }[] = [
    { id: "all_jobs", label: "Other Jobs", icon: <Briefcase className="w-4 h-4" /> },
    { id: "email_alerts", label: "LinkedIn Email Alerts", icon: <Mail className="w-4 h-4" /> },
    { id: "watchlist_leads", label: "LinkedIn Posts Leads", icon: <Telescope className="w-4 h-4" /> },
    { id: "upwork", label: "Upwork", icon: <Hammer className="w-4 h-4" /> },
    // Admin-only — manages the fake-job-poster blocklist enforced during LinkedIn Jobs/TheirStack ingestion.
    ...(isAdmin ? [{ id: "blocked_companies" as const, label: "Blocked Companies", icon: <Ban className="w-4 h-4" /> }] : []),
  ];

  const isAlerts = pageTab === "email_alerts";
  const hasFilters =
    search || employmentType || seniority ||
    (remoteScopeFilter !== "all" || statusFilter !== "new" || selectedManagerId || companyCanApplyOnly);

  const archivedCount = archivedPreview?.alert_count ?? 0;

  async function handleOldJobsOption(months: 6 | 12) {
    setOldJobsDropdownOpen(false);
    await openOldJobsCleanup(months);
  }

  return (
    <div className="space-y-5">
      {/* Page header — shown on every tab for a consistent title across the section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground font-inter">Job Board</h1>
          {isAlerts && (
            <p className="text-sm text-muted-foreground font-inter mt-0.5">
              {totalCount} job{totalCount !== 1 ? "s" : ""} tracked
            </p>
          )}
        </div>
        {isAlerts && (
          <div className="flex items-center gap-2">
            <ViewToggle view={view} onChange={switchView} />
            {/* Add LinkedIn Job by URL — admin (any TM) or a Technical Manager employee (self only) */}
            {(isAdmin || isTechnicalManager) && (
              <button
                onClick={() => setShowAddByUrl(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-primary hover:text-primary-foreground bg-primary/10 hover:bg-primary border border-primary/30 hover:border-primary rounded-xl transition-colors font-inter"
              >
                <Link2 className="w-4 h-4" />
                Add LinkedIn Job by URL
              </button>
            )}
            {/* Delete Old Jobs dropdown — admin only */}
            {isAdmin && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setOldJobsDropdownOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-red-400 border border-border hover:border-red-700/50 rounded-xl transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Old Jobs
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${oldJobsDropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {oldJobsDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-surface-muted border border-border rounded-xl shadow-xl z-20 overflow-hidden">
                    <button
                      onClick={() => handleOldJobsOption(6)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-surface-muted hover:text-foreground font-inter transition-colors"
                    >
                      Delete 6 Month+ Old Jobs
                    </button>
                    <button
                      onClick={() => handleOldJobsOption(12)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-surface-muted hover:text-foreground font-inter transition-colors"
                    >
                      Delete 1 Year+ Old Jobs
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => loadJobs(page)}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-end gap-1 border-b border-border pb-0">
        {pageTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setPageTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-inter rounded-t-lg transition-colors -mb-px border-b-2 ${
              pageTab === t.id
                ? "border-primary text-foreground bg-primary/10"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}

        {/* Employee filter — admin + LinkedIn Email Alerts only, floated right in the tab bar */}
        {isAlerts && isAdmin && (
          <div className="ml-auto flex items-center gap-2 pb-1">
            <label className="text-xs text-muted-foreground font-inter whitespace-nowrap">Employee:</label>
            <AppSelect
              value={selectedManagerId?.toString() ?? ""}
              onChange={(v) => setSelectedManagerId(v ? parseInt(v, 10) : null)}
              className="w-48"
            >
              <option value="">All TMs</option>
              {techManagers.map((e) => (
                <option key={e.id} value={e.id.toString()}>{e.name}</option>
              ))}
            </AppSelect>
          </div>
        )}
      </div>

      {/* ── Other Jobs (TheirStack) tab ── */}
      {pageTab === "all_jobs" && <TheirStackJobsPage isAdmin={isAdmin} />}

      {/* ── Watchlist Leads tab ── */}
      {pageTab === "watchlist_leads" && <WatchlistLeadsTab />}

      {/* ── Upwork tab ── */}
      {pageTab === "upwork" && <UpworkJobsPage isAdmin={isAdmin} />}

      {/* ── Blocked Companies tab — admin only ── */}
      {pageTab === "blocked_companies" && isAdmin && <BlockedCompaniesTab />}

      {/* ── LinkedIn Email Alerts ── */}
      {pageTab === "email_alerts" && <>

      {/* Setup guide banner */}
      <div className="flex items-center gap-3 px-4 py-3 bg-primary/8 border border-primary/20 rounded-xl">
        <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center shrink-0">
          <Play className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground font-inter font-medium leading-snug">New to LinkedIn Email Alerts?</p>
          <p className="text-xs text-muted-foreground font-inter mt-0.5">
            Watch our setup guide to learn how to create LinkedIn job alerts and forward them to the central inbox.
          </p>
        </div>
        <button
          onClick={() => setShowSetupVideo(true)}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary hover:text-primary-foreground bg-primary/20 hover:bg-primary border border-primary/30 hover:border-primary rounded-lg transition-colors font-inter whitespace-nowrap"
        >
          <Play className="w-3 h-3" />
          Watch Guide
        </button>
      </div>

      {/* ── Filters row ── */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-1 min-w-[200px] max-w-xs">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search title or company…"
              className="w-full bg-surface-muted border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground font-inter focus:outline-none focus:border-primary"
            />
          </div>
        </form>

        <AppSelect value={employmentType} onChange={setEmploymentType}>
          <option value="">All types</option>
          {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </AppSelect>

        <AppSelect value={seniority} onChange={setSeniority}>
          <option value="">All levels</option>
          {SENIORITY_LEVELS.map((s) => <option key={s} value={s}>{s}</option>)}
        </AppSelect>

        <AppSelect value={remoteScopeFilter} onChange={(v) => setRemoteScopeFilter(v as RemoteScopeFilter)}>
          <option value="worldwide">Remote Worldwide</option>
          <option value="unclear">Unclear</option>
          <option value="all">All Remote</option>
        </AppSelect>

        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none">
            <input
              type="checkbox"
              checked={companyCanApplyOnly}
              onChange={(e) => setCompanyCanApplyOnly(e.target.checked)}
              className="rounded border-border bg-surface-muted text-amber-500 focus:ring-amber-500"
            />
            Direct to Company only
          </label>
        )}
      </div>

      {/* Status chips row — a status filter doesn't make sense in board view,
          since the board already shows every status as its own column */}
      <div className="flex flex-wrap items-center gap-2">
        {view === "list" && (
          <>
            <span className="text-xs text-muted-foreground font-inter shrink-0">Status</span>
            {FILTER_STATUS_OPTIONS.map((o) => {
              const count = o.value === "" ? undefined : statusCounts[o.value];
              return (
                <button
                  key={o.value}
                  onClick={() => setStatusFilter(o.value)}
                  className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium transition-colors flex items-center gap-1.5 ${
                    statusFilter === o.value
                      ? STATUS_CHIP_SELECTED[o.value] ?? "bg-slate-500 text-white border-slate-400"
                      : "bg-surface-muted/60 text-muted-foreground border-border hover:border-border hover:text-foreground"
                  }`}
                >
                  {o.label}
                  {count !== undefined && count > 0 && (
                    <span className={`text-[10px] font-semibold px-1 py-0.5 rounded min-w-[16px] text-center leading-none ${
                      statusFilter === o.value
                        ? "bg-white/20 text-current"
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </>
        )}

        {/* Archived cleanup — admin only */}
        {isAdmin && (
          <div className="ml-auto flex items-center gap-2">
            {archivedPreviewLoading ? (
              <span className="text-xs text-muted-foreground font-inter">Loading…</span>
            ) : archivedCount > 0 ? (
              <span className="text-xs text-muted-foreground font-inter bg-surface-muted border border-border rounded-lg px-2 py-1">
                {archivedCount} archived
              </span>
            ) : null}
            <button
              onClick={() => { openArchivedCleanup(); }}
              disabled={archivedPreviewLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-400 hover:text-foreground border border-red-700/50 hover:border-red-600 hover:bg-red-600/10 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-inter"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clean Up Archived
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {loadError && (
        <div className="flex items-center gap-2 text-sm text-red-400 font-inter bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
          {loadError}
        </div>
      )}

      {/* Board view */}
      {view === "board" && (
        <StatusKanbanBoard
          columns={BOARD_COLUMNS}
          getId={(job) => job.alert_id ?? job.id}
          getStatus={(job) => job.status ?? "new"}
          withStatus={(job, status) => ({ ...job, status: status as JobStatus })}
          fetchColumnItems={fetchBoardColumn}
          onStatusChange={(job, status) => handleStatusUpdate(job.alert_id ?? job.id, status as JobStatus, null)}
          renderCard={(job) => (
            <JobCard
              job={job}
              onClick={() => setSelectedJob(job)}
              assignedName={
                isAdmin && !selectedManagerId && job.employee_id != null
                  ? employeeNameById.get(job.employee_id)
                  : undefined
              }
              techManagers={isAdmin ? techManagers : undefined}
              onReassign={isAdmin ? handleReassignAlert : undefined}
              onStatusChange={handleStatusUpdate}
              compact
            />
          )}
        />
      )}

      {/* Job grid */}
      {view === "list" && (loading && jobs.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground font-inter text-sm">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground font-inter">
          <Mail className="w-10 h-10 mb-3 opacity-40" />
          <p className="text-sm">No tracked jobs found</p>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="mt-2 text-xs text-primary hover:text-primary/80"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {jobs.map((job) => (
            <JobCard
              key={job.alert_id ?? job.id}
              job={job}
              onClick={() => setSelectedJob(job)}
              assignedName={
                isAdmin && !selectedManagerId && job.employee_id != null
                  ? employeeNameById.get(job.employee_id)
                  : undefined
              }
              techManagers={isAdmin ? techManagers : undefined}
              onReassign={isAdmin ? handleReassignAlert : undefined}
              onStatusChange={handleStatusUpdate}
            />
          ))}
        </div>
      ))}

      {/* Pagination */}
      {view === "list" && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => loadJobs(page - 1)}
            disabled={page <= 1 || loading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground font-inter">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => loadJobs(page + 1)}
            disabled={page >= totalPages || loading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {/* Detail modal */}
      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          isAdmin={isAdmin}
          onClose={() => setSelectedJob(null)}
          onSaved={() => loadJobs(page)}
        />
      )}

      {/* Add LinkedIn Job by URL modal */}
      {showAddByUrl && (
        <AddJobByUrlModal isAdmin={isAdmin} techManagers={techManagers} onClose={() => setShowAddByUrl(false)} />
      )}

      {/* Cleanup confirm modal */}
      {cleanupModal && (
        <CleanupConfirmModal
          modal={cleanupModal}
          executing={cleanupExecuting}
          error={cleanupError}
          onConfirm={executeCleanup}
          onClose={closeCleanupModal}
        />
      )}

      {/* Setup video modal */}
      {showSetupVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowSetupVideo(false)}
        >
          <div
            className="relative w-full max-w-3xl bg-surface border border-border rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <h2 className="text-base font-semibold text-foreground font-inter">LinkedIn Email Alerts — Setup Guide</h2>
                <p className="text-xs text-muted-foreground font-inter mt-0.5">
                  How to create LinkedIn job alerts and forward emails to the central inbox
                </p>
              </div>
              <button
                onClick={() => setShowSetupVideo(false)}
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                src="https://www.loom.com/embed/eac3813c39d049e69edb963da6367d12"
                frameBorder={0}
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                title="LinkedIn Email Alerts Setup Guide"
              />
            </div>
          </div>
        </div>
      )}

      </> /* end email_alerts tab */}
    </div>
  );
}
